
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import AsahiSelectHeader from '@/components/library/AsahiSelectHeader.vue'
import AsahiProgressBar from '@/components/library/assets/AsahiProgressBar.vue'
import { permissionSchema } from '@/store/modules/users/interfaces/IUser'
import { TransportationTypes } from '@/utils/transportTypes'
import { IAuthUser } from '@/store/modules/auth/interfaces/IAuthUser'
import DeliveryDatePicker from '@/components/cart/DeliveryDatePicker.vue'
import { isStandardDeliveryDate } from '@/utils/deliveryDate'
import shipToIcon from '@/components/library/assets/shipToBlack.svg'
import addToCartIcon from '@/components/library/assets/goldenPlus.svg'
import suggestedCartIcon from '@/components/library/assets/star.svg'
import trackIcon from '@/components/library/assets/Truck.svg'
import calendarIcon from '@/components/library/assets/CalendarWhite.svg'
import { BaseCartQueryItem } from '@/store/modules/basket/interfaces/cartQuery/ICartQueryResponse'
import ContinueShoppingModal from '@/components/modals/ContinueShoppingModal.vue'
import modals from '@/utils/modals'
import formatDateMixin from '@/utils/mixin'
import AsahiTypography from '@/components/library/Typography.vue'
import ErrorModal from '@/components/modals/ErrorModal.vue'
import SolidInfoIcon from '../library/icons/SolidInfoIcon.vue'
import AsahiInfoToolTip from '@/components/library/AsahiInfoToolTip.vue'
import localStorageCache from '@/utils/localStorageCache'
import { UsersMutationType } from '@/store/modules/users/interfaces/mutation-type'
import AsahiButton from '@/components/library/AsahiButton.vue'
import { showSuggestedCart } from '@/utils/user'

interface Link {
  name: string,
  label: string
  href?: string
  link?: string
  target?: string
  isActive?: boolean
}

interface HeaderData {
  label: string;
  children: any[];
  name: string;
  href?: string;
}

const MORE_SECTION_META_VALUES = ['marketing', 'news']
const CACHE_KEY = 'payers'
const CREATE_ORDER_NAME = 'Create an order'

export default defineComponent({
  components: {
    DeliveryDatePicker,
    AsahiSelectHeader,
    AsahiTypography,
    AsahiProgressBar,
    AsahiInfoToolTip,
    SolidInfoIcon,
    AsahiButton
  },
  mixins: [formatDateMixin, localStorageCache],
  props: {
    changeMessageColor: {
      type: Boolean,
      default: false
    },
    filterTransportations: {
      type: Array as () => Array<TransportationTypes>,
      default: undefined
    },
    isSuggestedCart: {
      type: Boolean,
      default: false
    },
    suggestedShipToId: {
      type: String,
      default: null
    },
    cartId: {
      type: String,
      default: ''
    },
    user: {
      type: Object,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      shipToIcon,
      trackIcon,
      calendarIcon,
      selectedShipmentId: '',
      errorMsg: '',
      successWeight: false,
      errorWeight: false,
      shipmentSet: false,
      selectedOrderType: 'selfpickup_selfpickup',
      deliveryDateSelected: '',
      shipTo: {},
      billingAddressForm: {} as any,
      options: [] as any,
      deliveryDatePickerHeight: 0,
      isExpanded: false,
      bufferedSelection: {} as any,
      isDatePickerOpen: false,
      apiLocked: false,
      addToCartIcon,
      suggestedCartIcon,
      addToCartLabel: '',
      hideCta: false
    }
  },
  computed: {
    ...mapState('basket', ['id', 'query', 'deliveryDate', 'items', 'paymentMethodCode', 'paymentMethod', 'availableDate', 'availableFeeDate', 'availableDays', 'filteredDeliveryDates', 'hasCalendarException']),
    ...mapState('catalog', ['alignment', 'shipTos', 'favorites', 'favoritesInfo', 'queries']),
    ...mapState('main', ['holidaysConfig']),
    ...mapState('auth', ['user']),
    ...mapState('basket', ['isGroupedViewEnabled']),

    getShipmentName () {
      return this.shipToOptions.find((s: any) => s.value === this.selectedShipmentId)?.label
    },

    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },

    showSuggestedCart,

    isCatalogVisible (): boolean {
      return true
    },

    isMoreSection (): boolean {
      return MORE_SECTION_META_VALUES.includes(this.$route?.meta?.tabSelection as string)
    },

    formattedDeliveryDate () {
      if (!this.deliveryDate) return ''
      const args = this.deliveryDate.split('-')
      return args[2] + '.' + args[1]
    },
    currentShippingPlace () {
      return this.shipToOptions.find((s: any) => s.value === this.selectedShipmentId)
    },
    shipToOptions () {
      const { permissionSchema = [] } = this.user || {}
      return permissionSchema
        .filter((permission: any) =>
          permission.rpDetails.some((role: any) => role.permission.includes('Catalogue'))
        )
        .map((permission: any) => ({
          label: permission.shipToName,
          value: permission.shipToId,
          description: permission?.shipToAddress
        }))
    },
    progressBar () {
      return this.query?.progress_bar_data
    },
    maxOrderCash () {
      return this.progressBar?.iMaximumOrderCash ?? 0
    },
    grandTotalYourPrice () {
      return this.query?.grand_total_your_price_details?.grand_total_your_price ?? 0
    },
    cashFilledPercentage () {
      return (this.grandTotalYourPrice / this.maxOrderCash) * 100
    },
    defaultShipTo () {
      return this.$store.state.auth.defaultShipTo
    },
    messageColorClass () {
      let msgColor = ''
      let borderColor = ''
      if (this.changeMessageColor) {
        if (this.errorWeight === true) {
          msgColor = 'error-text'
          borderColor = 'border-error-text'
        } else if (this.successWeight === true) {
          msgColor = 'success-text'
          borderColor = 'border-success-text'
        } else {
          msgColor = 'default-text'
          borderColor = 'border-default-text'
        }
      }
      return { msgColor, borderColor }
    },
    cartItems () {
      let cartItems: Array<{ productid: string }> = []
      if (this.items.length) {
        cartItems = this.items.map((item: BaseCartQueryItem) => {
          return { productId: item.product.sku }
        })
      }
      return cartItems
    },
    isOnHomepage () {
      return this.$route.path === '/'
    },
    isPackagePage () {
      return this.$route.path === '/catalog/packages'
    },
    isFavoritesPage () {
      return this.$route.path === '/catalog/favorites'
    },
    isSuggestedBasketPage () {
      return this.$route.path === '/catalog/suggested-cart'
    }
  },
  methods: {
    extractCatalogData (data: HeaderData[]) {
      const findByName = (list: HeaderData[]): HeaderData | undefined => {
        for (const item of list) {
          if (item?.name === CREATE_ORDER_NAME) return item
          if (item?.children?.length) {
            const found = findByName(item.children)
            if (found) return found
          }
        }
      }
      const target = findByName(data)
      this.addToCartLabel = target?.label || ''
    },
    navigateToPath (link: Link) {
      this.$gtm.trackEvent({ ecommerce: null })
      this.$gtm.trackEvent({
        event: 'click_interaction',
        action: link.label,
        component: 'header'
      })

      this.$navigate(link.href || link.link || '')
    },
    handleDatePickerOpen () {
      this.isDatePickerOpen = true
    },
    handleDatePickerClose () {
      this.isDatePickerOpen = false
    },
    toggleAccordion (event: any, index: number) {
      if (event.target.getAttribute('aria-expanded') === 'true') {
        this.isExpanded = true
      } else {
        this.isExpanded = false
      }
    },
    async setShipmentWrapper (value: string) {
      if (this.checkDisableShipTo(this.user, value)) {
        modals.showModal({
          component: ErrorModal,
          props: {
            title: this.$t('ShipTo:'),
            errorMessages: this.$t('Current Your are not able to place an Order, please contact customer service'),
            hideCta: false
          }
        })
        return
      }
      if (this.items?.length > 0) {
        this.setContinueModalMessages(
          this.$t('Do you want to proceed with the current order or start a new one?', {
            place: this.currentShippingPlace?.description || '',
            date: this.formateDateWithoutLocale(this.deliveryDateSelected)
          }),
          this.$t('Continue the current order for the new selected sold to.', { place: this.currentShippingPlace?.description || '' }),
          this.$t('Start a new order with the new sold to. Products from the cart will be removed.', { place: this.getShippingDescription(value) }))
        this.bufferedSelection = { type: 'setShipment', value }
        return this.openOrderModal()
      } else {
        this.setContinueModalMessages('', '', '')
        this.setShipment(value, true)
      }
    },

    getShippingDescription (value: string) {
      const shipTo = this.shipToOptions.find((option: any) => option.value === value)
      return shipTo?.description || ''
    },

    async setShipment (value: string, isDropDownChange = false) {
      if (!value) return
      this.errorMsg = ''
      this.selectedShipmentId = value
      localStorage.setItem('shipToId', this.selectedShipmentId)
      const companyId = this.getCompanyid(this.selectedShipmentId, this.user)
      try {
        await this.$store.commit('basket/SET_LOADING', true, { root: true })
        const cache = await this.getCache(CACHE_KEY + companyId)
        if (cache) {
          this.$store.commit(UsersMutationType.SET_PAYER_DETAILS, cache)
        } else {
          this.$store.dispatch('users/FETCH_PAYER_DETAILS', { id: encodeURIComponent(companyId) }, { root: true })
          this.setCache(CACHE_KEY + companyId, this.$store.state.users?.payerDetails?.payers)
        }

        this.onShipToSelection(isDropDownChange)
        if (this.isSuggestedBasketPage) {
          if (this.isGroupedViewEnabled) {
            await this.$store.dispatch('basket/FETCH_SUGGESTED_CART_GROUPED', { ship_tos: [this.selectedShipmentId], group_view: false })
          } else {
            await this.$store.dispatch('basket/FETCH_SUGGESTED_CART', { ship_tos: [this.selectedShipmentId], group_view: false })
          }
          await this.$store.dispatch('basket/FETCH_SUGGESTED_CART', { ship_tos: [this.selectedShipmentId] })
        }
        await this.$store.dispatch('catalog/FETCH_RECOMMENDED_PRODUCTS', {
          shipTo: this.selectedShipmentId,
          cartItems: this.cartItems
        }, { root: true })
      } catch (error) {
        console.error('shipment error', error)
        this.errorMsg = 'Error setting shipment'
      } finally {
        await this.$store.commit('basket/SET_LOADING', false, { root: true })
      }

      const event = new CustomEvent('setShipment', {
        detail: {
          type: 'delivery-options',
          shipTo: this.selectedShipmentId,
          payer: companyId
        }
      })
      document.dispatchEvent(event)
    },
    onResizeDeliveryDatePicker ({ height }: {
      height: number
    }) {
      this.deliveryDatePickerHeight = height
    },
    getCompanyid (shipToId: string, user: IAuthUser) {
      let companyId = ''
      const shipToItem = user?.permissionSchema.find((item: permissionSchema) => item.shipToId === shipToId)
      if (shipToItem) {
        companyId = shipToItem?.companyId
      }
      return companyId
    },
    checkDisableShipTo (user: IAuthUser, shipToId: string) {
      const permissionSchema = user?.permissionSchema || []
      if (shipToId != null) {
        return permissionSchema.some(
          (shipToItem: permissionSchema) => shipToItem.shipToId === shipToId && shipToItem?.shipToStatus?.toLowerCase() === 'inactive'
        )
      }

      if (permissionSchema.length === 1) {
        return permissionSchema[0]?.shipToStatus === 'SUSPENDED'
      }

      return false
    },
    async startYourOrder () {
      const errorMsg = []
      if (this.selectedOrderType === '') errorMsg.push(this.$t('Order type has to not be empty!'))
      if (this.deliveryDateSelected === '') errorMsg.push(this.$t('Delivery date has to not be empty!'))
      if (errorMsg.length > 0) {
        // this.errorMsg = errorMsg[0]
        return
      }
      if (this.deliveryDateSelected == null || this.deliveryDateSelected === '') {
        (this.$refs.deliveryDatePickerRef as InstanceType<typeof DeliveryDatePicker>)?.forceRecalculate()
        errorMsg.push(this.$t('Shipping cannot be ordered. Choose a different date.'))
      }
      if (errorMsg.length > 0) {
        this.errorMsg = errorMsg[0]
        return
      }

      if (!this.apiLocked) {
        // get new orderable units based on new order type
        this.$store.dispatch('main/FETCH_ORDERABLE_UNITS_CONFIG',
          {
            customerType: localStorage.getItem('customertype'),
            orderType: this.selectedOrderType
          }, { root: true })
      }

      await this.$store.dispatch('basket/FETCH_SUGGESTED_CART', { ship_tos: [localStorage.getItem('shipToId')], search: '' })

      localStorage.setItem('deliveryDate', this.deliveryDateSelected)
      localStorage.setItem(
        'standardDeliveryDate',
        Number(isStandardDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos)).toString()
      )
      const customerType = localStorage.getItem('customertype')

      this.$store.commit('basket/SET_DELIVERY_DATE', this.deliveryDateSelected, { root: true })
      if (!this.apiLocked) {
        await this.$store.dispatch(
          'basket/FETCH_TRANSPORT_TYPE',
          {
            transporttype: customerType, // currently don't have transport type available
            cart_id: this.id,
            customertype: customerType,
            sappaymentcode: this.paymentMethodCode,
            sappaymentmethod: this.paymentMethod
          },
          { root: true }
        )
      }

      try {
        if (!this.apiLocked) {
          this.fetchCatalog()
          this.apiLocked = true
        }
        await this.$store.dispatch('basket/FETCH_ORDER_REASON', { soldToId: this.selectedShipmentId, deliveryDate: this.deliveryDateSelected }, { root: true })
      } catch (error) {
        console.error('order modal error', error)
      }

      this.apiLocked = false
    },
    async initDefaultShipmentId () {
      const selectedShipTo = localStorage.getItem('shipToId') ?? ''
      if (this.shipToOptions.length > 0 && selectedShipTo === '') {
        let index = 0
        let selected = false
        while (index < this.shipToOptions.length && !selected) {
          if (!this.checkDisableShipTo(this.user, this.shipToOptions[index].value)) {
            this.setShipment(this.shipToOptions[index].value)
            selected = true
          }
          index++
        }
      } else {
        if (!this.checkDisableShipTo(this.user, selectedShipTo) && this.checkShipToExists(selectedShipTo)) {
          this.setShipment(selectedShipTo)
        } else {
          let index = 0
          let selected = false
          while (index < this.shipToOptions.length && !selected) {
            if (!this.checkDisableShipTo(this.user, this.shipToOptions[index].value)) {
              this.setShipment(this.shipToOptions[index].value)
              selected = true
            }
            index++
          }
        }
      }
      if (this.holidaysConfig == null || this.holidaysConfig?.holidays?.length === 0) await this.$store.dispatch('main/FETCH_HOLIDAYS_CONFIG', { root: true })
    },

    checkShipToExists (shipToId: string) {
      const permissionSchema = this.user?.permissionSchema || []
      if (shipToId != null) {
        return permissionSchema.find(
          (shipToItem: permissionSchema) => shipToItem.shipToId === shipToId
        )
      }
    },

    async onShipToSelection (isDropDownChange = false) {
      const alreadySetDeliveryDate = localStorage.getItem('deliveryDate') ?? ''
      const fromLocal = localStorage.getItem('shipToId') || ''
      const selectedShipTo = fromLocal || this.selectedShipmentId
      // await this.$store.commit('basket/SET_SHIPMENT_ID', this.selectedOrderType, { root: true })
      const storeId = localStorage.getItem('store')
      if (storeId != null) {
        await this.$store.dispatch('basket/GET_DELIVERY_AVAILABLE_DATE', {
          soldToId: selectedShipTo,
          store: storeId
        })
        await this.fetchFirstAvailableDate()
        // Check is already selected delivery date exist in available days array
        const doesExistPreSelectedDate = this.availableDays.filter((d: string) => alreadySetDeliveryDate !== '' && d.includes(alreadySetDeliveryDate))
        if (doesExistPreSelectedDate.length > 0 && !isDropDownChange) {
          this.deliveryDateSelected = alreadySetDeliveryDate
        } else {
          const firstAvailableDate = this.filteredDeliveryDates.length > 0 ? this.filteredDeliveryDates[0] : new Date().toISOString()
          this.deliveryDateSelected = this.availableDate.slice(0, 10)
        }

        this.startYourOrder()
      }
    },

    async fetchFirstAvailableDate () {
      await this.$store.dispatch('basket/GET_FIRST_AVAILABLE_DATE', { soldToId: this.selectedShipmentId })
      let availableDate
      if (this.availableDate) {
        availableDate = new Date(this.availableDate)
      } else {
        return
      }
      const availableDateFormatted = `${availableDate.getFullYear()}-${('0' + (availableDate?.getMonth() + 1))?.slice(-2)}-${('0' + availableDate.getDate()).slice(-2)}`
      this.onDateSelection(availableDateFormatted)
    },

    openOrderModal () {
      modals.showModal({
        component: ContinueShoppingModal,
        props: {
          isShipping: this.bufferedSelection.type === 'setShipment'
        },
        listeners: {
          doSelection: () => {
            this.doSelection()
            this.setContinueModalMessages('', '', '')
          }
        }
      })
    },

    setContinueModalMessages (title: string, continueMsg: string, newMsg: string) {
      this.$store.commit('catalog/SET_CONTINUE_MODAL_TITLE', title)
      this.$store.commit('catalog/SET_CONTINUE_MODAL_CONTINUE_MESSAGE', continueMsg)
      this.$store.commit('catalog/SET_CONTINUE_MODAL_NEW_MESSAGE', newMsg)
    },

    doSelection () {
      if (this.bufferedSelection?.type && this.bufferedSelection.value) {
        switch (this.bufferedSelection?.type) {
          case 'setShipment' :
            this.setShipment(this.bufferedSelection.value, true)
            break
          case 'onDateSelection' :
            this.onDateSelection(this.bufferedSelection.value)
            break
        }
      }
      this.bufferedSelection = {}
    },

    onDateSelectionWrapper (valueWrapper: any) {
      const value = typeof valueWrapper === 'string' ? valueWrapper : valueWrapper.value
      this.deliveryDateSelected = localStorage.getItem('deliveryDate') ?? this.filteredDeliveryDates[0]
      if (this.items?.length > 0 && !valueWrapper.isFirstAvailable) {
        (this.$refs.deliveryDatePickerRef as InstanceType<typeof DeliveryDatePicker>)?.forceRecalculate()
        this.setContinueModalMessages(
          this.$t('Do you want to proceed with the current order or start a new one?', {
            place: this.currentShippingPlace?.description || '',
            date: this.formateDateWithoutLocale(this.deliveryDateSelected)
          }),
          this.$t('Continue the current order for the new selected delivery date.'),
          this.$t('Start a new order with the new delivery date. Products from the cart will be removed.', { date: this.formateDateWithoutLocale(value) }))
        this.bufferedSelection = { type: 'onDateSelection', value }
        return this.openOrderModal()
      } else {
        this.setContinueModalMessages('', '', '')
        this.onDateSelection(value)
      }
    },
    async onDateSelection (valueWrapper: any) {
      const value = typeof valueWrapper === 'string' ? valueWrapper : valueWrapper.value
      this.deliveryDateSelected = value
      this.$store.commit('basket/SET_DELIVERY_DATE', this.deliveryDateSelected, { root: true })
      if (!valueWrapper?.isFirstAvailable) {
        await this.$store.dispatch('basket/FETCH_ORDER_REASON', { soldToId: localStorage.getItem('shipToId') || this.selectedShipmentId, deliveryDate: this.deliveryDateSelected }, { root: true })
      }
      this.startYourOrder()
    },
    async fetchCatalog () {
      const ajaxRequests = [
        this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.id }, { root: true })
      ]
      if (this.isPackagePage) {
        ajaxRequests.push(await this.$store.dispatch('catalog/FETCH_PACKAGE_LIST', {
          packages_flag: 1,
          fetchPolicy: 'network-only'
        }, { root: true }))
      } else if (this.isFavoritesPage) {
        await this.$store.dispatch('catalog/FETCH_FAVORITES', {
          ...this.queries,
          pageSize: this.favoritesInfo?.pageSize
        }, { root: true })
      } else if (!this.isOnHomepage) {
        ajaxRequests.push(this.$store.dispatch('catalog/FETCH_PRODUCTS', { fetchPolicy: 'network-only' }, { root: true }))
      }

      this.$store.dispatch('catalog/FETCH_WISHLIST', { bypassloading: true }, { root: true })
      await Promise.all(ajaxRequests)
    },
    handelShipToUpdatedEvent () {
      this.initDefaultShipmentId()
    }

  },
  mounted () {
    // this.initDefaultShipmentId()
    window.addEventListener('shipToUpdated', () => {
      this.initDefaultShipmentId()
    })
    this.extractCatalogData(this.portalPageMenuLink)
  },
  beforeUnmount () {
    window.removeEventListener('shipToUpdated', () => {
      this.handelShipToUpdatedEvent()
    })
  },
  watch: {
    availableDate (newDate) {
      this.deliveryDate = newDate
    },
    selectedShipTo (newVal) {
      this.selectedShipmentId = newVal
    },
    $route: {
      handler (nextVal) {
        this.hideCta = nextVal?.meta?.hideCreateOrderButton as boolean || false
      },
      immediate: true,
      deep: true
    },
    defaultShipTo (defaultShipTo) {
      if (defaultShipTo) {
        const parseShipTo = parseInt(defaultShipTo).toString()

        localStorage.setItem('shipToId', parseShipTo)
        this.initDefaultShipmentId()
      }
    },
    portalPageMenuLink (newData) {
      this.extractCatalogData(newData)
    }
  }
})
